import { useState, useEffect, useCallback } from 'react';
import { adminClient } from '@/api/adminClient';
import type { Room, Connection, RoomMetrics } from '@/types';

const POLL_INTERVAL = 5000; // 5 seconds

export function useRoomStatus() {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [metrics, setMetrics] = useState<RoomMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [roomsData, connectionsData, metricsData] = await Promise.all([
        adminClient.getRooms(),
        adminClient.getConnections(),
        adminClient.getRoomMetrics(),
      ]);

      setRooms(roomsData);
      setConnections(connectionsData);
      setMetrics(metricsData);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch room status:', err);
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();

    const interval = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchData]);

  const toggleRoom = useCallback(async (roomId: string, enabled: boolean) => {
    try {
      const updatedRoom = await adminClient.toggleRoom(roomId, enabled);
      setRooms((prev) =>
        prev.map((room) => (room.id === roomId ? updatedRoom : room))
      );
    } catch (err) {
      console.error('Failed to toggle room:', err);
      throw err;
    }
  }, []);

  const disconnectUser = useCallback(async (connectionId: string) => {
    try {
      await adminClient.disconnectConnection(connectionId);
      setConnections((prev) =>
        prev.filter((conn) => conn.id !== connectionId)
      );
    } catch (err) {
      console.error('Failed to disconnect user:', err);
      throw err;
    }
  }, []);

  return {
    rooms,
    connections,
    metrics,
    loading,
    error,
    toggleRoom,
    disconnectUser,
    refresh: fetchData,
  };
}
